import { createAsyncThunk, ActionReducerMapBuilder } from "@reduxjs/toolkit";
import { AICodingClient, AICodingJobDto } from "app/V2ApiClient";
import { SortDirections, SorterDescriptor } from "app/ApiClient";
import { LoadingState } from "types";
import { AICodingState } from "./aiCodingState";
import { mapJobDetailsToVariableVM } from "./aiCodingMapper";
import { RootState } from "@store";

const client = new AICodingClient(process.env.REACT_APP_API_URL);
const API_VERSION = "2";

export const getAICodingJob = createAsyncThunk(
  "aiCodingV2/getAICodingJob",
  async (jobConfigId: number, { rejectWithValue }) => {
    try {
      return await client.getAICodingJobDetails(jobConfigId, API_VERSION);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const sortAICodingJobs = (jobs: AICodingJobDto[], sortList?: SorterDescriptor[]): AICodingJobDto[] => {
  if (sortList && sortList.length > 0) {
    let sortedJobs: AICodingJobDto[] = [...jobs];
    for (const sortDesc of sortList) {
      const sortKey = sortDesc.field as keyof AICodingJobDto;
      sortedJobs = sortedJobs.sort((a, b) => {
        const dataA = a[sortKey]?.toString();
        const dataB = b[sortKey]?.toString();

        const sortDirection: SortDirections = sortDesc.directions ?? SortDirections.Desceding;

        if (dataA && dataB) {
          return sortDirection === SortDirections.Asceding ? dataA.localeCompare(dataB) : dataB.localeCompare(dataA);
        }
        if (dataA && !dataB) {
          return sortDirection === SortDirections.Asceding ? 1 : -1;
        }
        if (!dataA && dataB) {
          return sortDirection === SortDirections.Asceding ? -1 : 1;
        }
        return 0;
      });
    }
    return sortedJobs;
  }
  return jobs;
};

export const getAICodingJobs = createAsyncThunk(
  "aiCoding/getAICodingJobs",
  async (
    { projectId, encodedVersionId }: { projectId: string; encodedVersionId: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const { aiCoding } = getState() as RootState;
      const allJobs = await client.getAICodingJobs(projectId, encodedVersionId, "2");
      // Apply sorting only - filtering is done client-side in the component
      const sortedJobs = sortAICodingJobs(allJobs, aiCoding.pageContext.sortList);
      return sortedJobs;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);


export const addV2Reducers = (builder: ActionReducerMapBuilder<AICodingState>) => {
  builder
    .addCase(getAICodingJob.pending, (state) => {
      state.aiCodingJobDetailsLoading = LoadingState.pending;
    })
    .addCase(getAICodingJob.fulfilled, (state, action) => {
      state.aiCodingJobDetailsLoading = LoadingState.succeed;
      state.aiCodingJobDetailsDto = action.payload;
      state.variableItemVM = mapJobDetailsToVariableVM(action.payload);
      state.variableStatsLoading = LoadingState.idle;
    })
    .addCase(getAICodingJob.rejected, (state) => {
      state.aiCodingJobDetailsLoading = LoadingState.failed;
    })
    .addCase(getAICodingJobs.pending, (state) => {
      state.aiCodingJobsLoading = LoadingState.pending;
    })
    .addCase(getAICodingJobs.fulfilled, (state, action) => {
      state.aiCodingJobsLoading = LoadingState.succeed;
      state.allAiCodingJobs = action.payload;
    })
    .addCase(getAICodingJobs.rejected, (state) => {
      state.aiCodingJobsLoading = LoadingState.failed;
    });
};
